/**
 * Ownership rules for browser state that belongs to ONE signed-in account.
 *
 * None of the app's storage keys are namespaced by user, and the only sweep that clears
 * them runs in the explicit sign-out handler. Clerk session expiry, a sign-out from
 * Clerk's own UI, and a sign-out in another tab all bypass it — so on a shared browser
 * an unfinished exam, a question-frequency map, or a cached profile written by one
 * account is silently adopted by the next account to sign in.
 *
 * Two independent guards close that, and they are deliberately separate because they
 * cover different failure paths:
 *
 *   1. `resolveIdentityTransition` — compares the account that last used this browser
 *      against the one signing in now, and clears everything when they differ. Catches
 *      state that carries no owner of its own.
 *   2. `resolveActiveTestOwnership` — validates the owner stamped INTO the persisted
 *      exam. Catches the case where the sweep never ran at all.
 */

export type IdentityTransition =
  /** Same account as last time — keep everything. */
  | "same-user"
  /** A different account than last time — user-scoped state must be cleared. */
  | "new-user"
  /**
   * No account has been recorded for this browser yet.
   *
   * Deliberately NOT treated as a transition. Every existing installation reaches this
   * exactly once, on the first load after this code ships, and clearing there would
   * delete the in-progress exam of every student mid-attempt at deploy time.
   *
   * This leaves ONE accepted residual: a browser with no recorded identity, holding an
   * unstamped exam, into which a different account signs in, will adopt that exam. It is
   * not closed — `resolveActiveTestOwnership` adopts unstamped payloads rather than
   * discarding them, because discarding costs every mid-attempt student their work on
   * deploy day. The window is at most one sign-in per browser: the stamp and the
   * recorded identity are both written immediately afterwards.
   */
  | "first-known-user";

export function resolveIdentityTransition(
  storedUserId: string | null | undefined,
  currentUserId: string | null | undefined
): IdentityTransition {
  if (!currentUserId) return "same-user";
  if (!storedUserId) return "first-known-user";
  return storedUserId === currentUserId ? "same-user" : "new-user";
}

export function didAuthenticatedIdentityChange(
  previousUserId: string | null | undefined,
  currentUserId: string | null | undefined
): boolean {
  return Boolean(previousUserId && previousUserId !== currentUserId);
}

export type ActiveTestOwnership =
  /** Stamped with the signed-in account — restore it. */
  | "owned"
  /** Stamped with a DIFFERENT account — discard without reading its contents. */
  | "foreign"
  /**
   * Written before exams carried an owner stamp.
   *
   * ADOPTED — claimed for the signed-in account and stamped — rather than discarded.
   *
   * Discarding was the original choice and it was wrong. The reasoning behind it ("an
   * unstamped payload only survives until the student's next answer, because every
   * saveState() re-stamps it") had the order backwards: ownership is enforced during
   * boot, BEFORE the student can answer anything. So discarding would delete the
   * in-progress exam of every student who was mid-attempt when this shipped, on their
   * very next page load, while they were still the same person.
   *
   * Adopting is safe because it is not the only guard. A genuine account change is
   * caught by `resolveIdentityTransition`, which clears ALL user-scoped storage —
   * including the stored exam — before this is ever consulted. The residual is one
   * narrow window: a browser with no recorded identity, holding an unstamped exam, into
   * which a DIFFERENT account signs in. That can happen at most once per browser,
   * because both the stamp and the recorded identity are written immediately after.
   */
  | "unstamped";

export function resolveActiveTestOwnership(
  stampedOwnerId: string | null | undefined,
  currentUserId: string | null | undefined
): ActiveTestOwnership {
  if (!currentUserId) return "foreign";
  if (!stampedOwnerId) return "unstamped";
  return stampedOwnerId === currentUserId ? "owned" : "foreign";
}

/** Whether the persisted exam must be dropped for the account now signed in. */
export function shouldDiscardActiveTest(
  stampedOwnerId: string | null | undefined,
  currentUserId: string | null | undefined
): boolean {
  return resolveActiveTestOwnership(stampedOwnerId, currentUserId) === "foreign";
}

/** Whether the exam is unowned and should be claimed for the signed-in account. */
export function shouldAdoptActiveTest(
  stampedOwnerId: string | null | undefined,
  currentUserId: string | null | undefined
): boolean {
  return resolveActiveTestOwnership(stampedOwnerId, currentUserId) === "unstamped";
}
